// src/SearchByDate.jsx
// COPY/PASTE READY — ADVANCED RESPONSIVE VERSION
// Preserves backend relations/endpoints:
// - /table-search/by-date (preferred: include_unmerged=1&scan_sibling_tables=1)
// - /table-search/by-date-unmerged (optional compatibility fallback)
// - /customer-service/send-by-date
// - /customer-service/test-send
// - /customer-service/status/:jobId
// - /customer-service/stop/:jobId
// - /table-search/export-vcf
// - /table-search/export-csv
// - /update-company-by-date
// - /update-contact
// - /delete-contact
// Works on desktop, tablet, mobile, and modern browsers.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { API, getActiveBranch } from "./apiBase";
import { useAuth } from "./context/AuthContext";
import MessageHistory, { newRetryKey, historyUserKey } from "./MessageHistory";
import ReplyTracker from "./ReplyTracker";
import Bv1FollowUp from "./Bv1FollowUp";

const SEND_PASSWORD = "Fouad@123";
const TEST_SEND_PHONE = "96171565383";

const BRANCHES = [
  { value: "jounieh", label: "Jounieh" },
  { value: "tripoli", label: "Tripoli" },
  { value: "batroun", label: "Batroun" },
  { value: "zgharta", label: "Zgharta" },
  { value: "ehden", label: "Ehden" },
];

const C = {
  page: "#07111f",
  bg: "#0b1220",
  card: "#111827",
  card2: "#0f172a",
  panel: "#111827",
  border: "rgba(255,255,255,0.10)",
  borderStrong: "rgba(59,130,246,0.35)",
  text: "#e5e7eb",
  muted: "#94a3b8",
  textDim: "#cbd5e1",
  accent: "#3b82f6",
  accentSoft: "rgba(59,130,246,0.16)",
  danger: "#ef4444",
  dangerSoft: "rgba(239,68,68,0.14)",
  success: "#10b981",
  successSoft: "rgba(16,185,129,0.14)",
  warning: "#f59e0b",
  warningSoft: "rgba(245,158,11,0.16)",
  tableText: "#0f172a",
};

function todayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function inclusiveDateRange(from, to) {
  const valid = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) > 0 &&
    !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
    new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
  if (!valid(from) || !valid(to)) throw new Error("Choose a valid From date and To date.");
  if (from > to) throw new Error("The To date must be on or after the From date.");
  const count = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;
  if (count > 366) throw new Error("Choose a range of 366 days or fewer.");
  return Array.from({ length: count }, (_, index) =>
    new Date(Date.parse(`${from}T00:00:00Z`) + index * 86400000).toISOString().slice(0, 10));
}

export function dateSearchRecipientPhone(value) {
  const normalize = (raw) => {
    let phone = String(raw || "").replace(/\D/g, "").replace(/^00/, "");
    if (phone.startsWith("9610")) phone = `961${phone.slice(4)}`;
    if (phone.startsWith("0") && phone.length >= 8) phone = `961${phone.slice(1)}`;
    if (!phone.startsWith("961") && [7, 8].includes(phone.length)) phone = `961${phone}`;
    return phone;
  };
  const candidates = (Array.isArray(value) ? value : [value])
    .flatMap((part) => String(part || "").split(/[,;|\/\\\r\n\t]+/))
    .map(normalize).filter(Boolean);
  return candidates.find((phone) => /^\d{10,15}$/.test(phone) && new Set(phone).size > 2) || candidates[0] || "";
}

function contactIdentity(row) {
  const phone = dateSearchRecipientPhone(row?.phones || row?.phone || "");
  return phone ? `phone:${phone}` : `name:${String(row?.name || "").trim().toLowerCase()}|birthday:${row?.birthday || ""}|id:${row?.source_db || ""}:${row?._id ?? row?.id ?? ""}`;
}

export function mergeDateRangeRows(groups) {
  const merged = new Map();
  for (const row of groups.flat()) {
    const key = contactIdentity(row);
    const previous = merged.get(key);
    const matched = Array.from(new Set([...(previous?.matched_dates || []), ...(row.matched_dates || []), row.search_date].filter(Boolean))).sort();
    merged.set(key, { ...(previous || row), search_date: matched[0] || row.search_date || "", matched_dates: matched });
  }
  return Array.from(merged.values());
}

export function selectedRowsCSV(rows) {
  const escape = (value) => {
    let text = String(value ?? "");
    if (/^[\s]*[=+@-]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  const fields = ["name", "company", "phones", "birthday", "added_at", "search_date", "source_db"];
  return [fields, ...rows.map((row) => fields.map((field) => row[field] || ""))]
    .map((row) => row.map(escape).join(",")).join("\r\n");
}

export function selectedRowsVCF(rows) {
  const escape = (value) => String(value ?? "").replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/;/g, "\\;").replace(/,/g, "\\,");
  return rows.map((row) => {
    const phones = (Array.isArray(row.phones) ? row.phones : [row.phones || row.phone])
      .flatMap((value) => String(value || "").split(/[,;|\/\r\n]+/))
      .map((value) => value.trim()).filter(Boolean);
    return ["BEGIN:VCARD", "VERSION:3.0", `FN:${escape(row.name || "Guest")}`,
      `ORG:${escape(row.company)}`, ...phones.map((phone) => `TEL;TYPE=CELL:${escape(phone)}`),
      "END:VCARD"].join("\r\n");
  }).join("\r\n") + "\r\n";
}

function num(n) {
  return Number(n || 0).toLocaleString();
}

function pct(job) {
  const total = Number(job?.total || 0);
  if (!total) return 0;

  const done =
    Number(job?.sent || 0) +
    Number(job?.failed || 0) +
    Number(job?.skipped || 0);

  return Math.max(0, Math.min(100, Math.round((done * 100) / total)));
}

function getCompleted(job) {
  return (
    Number(job?.sent || 0) +
    Number(job?.failed || 0) +
    Number(job?.skipped || 0)
  );
}

function useViewport() {
  const get = () => {
    if (typeof window === "undefined") {
      return {
        width: 1440,
        height: 900,
        isMobile: false,
        isTablet: false,
      };
    }

    const width = window.innerWidth || 1440;
    const height = window.innerHeight || 900;

    return {
      width,
      height,
      isMobile: width <= 760,
      isTablet: width > 760 && width <= 1100,
    };
  };

  const [vp, setVp] = useState(get);

  useEffect(() => {
    let raf = 0;

    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setVp(get()));
    };

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return vp;
}

function safeText(v, fallback = "—") {
  const t = String(v ?? "").trim();
  return t || fallback;
}

function WhatsAppPhone({ value, color = "#047857", fallback = "—" }) {
  const phones = (Array.isArray(value) ? value : [value])
    .flatMap((part) => String(part ?? "").split(/[,;|\/\r\n]+/))
    .map((part) => part.trim())
    .filter(Boolean);

  if (!phones.length) return <span>{fallback}</span>;

  return (
    <span style={{ overflowWrap: "anywhere" }}>
      {phones.map((display, i) => {
        const phone = display.replace(/\D+/g, "").replace(/^00/, "");

        return (
          <React.Fragment key={`${display}-${i}`}>
            {i > 0 && ", "}

            {phone ? (
              <a
                href={`https://wa.me/${phone}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open WhatsApp chat with ${display}`}
                aria-label={`Open WhatsApp chat with ${display}`}
                style={{
                  color,
                  fontWeight: 800,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {display}
              </a>
            ) : (
              display
            )}
          </React.Fragment>
        );
      })}
    </span>
  );
}

function CustomerMobileCard({
  row,
  checked,
  onToggle,
  onEdit,
  onDelete,
  deleting,
  btn,
  C,
}) {
  return (
    <div
      style={{
        background: "#ffffff",
        color: "#0f172a",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 12,
        boxShadow: "0 10px 24px rgba(15,23,42,.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <label
          style={{
            display: "flex",
            gap: 9,
            alignItems: "flex-start",
            minWidth: 0,
            flex: 1,
          }}
        >
          <input
            type="checkbox"
            aria-label={`Select ${safeText(row.name, "guest")}`}
            checked={checked}
            onChange={onToggle}
            style={{ marginTop: 4, transform: "scale(1.2)" }}
          />

          <span style={{ minWidth: 0 }}>
            <b
              style={{
                display: "block",
                fontSize: 16,
                wordBreak: "break-word",
              }}
            >
              {safeText(row.name)}
            </b>

            <span
              style={{
                display: "block",
                fontSize: 13,
                color: "#475569",
                wordBreak: "break-word",
              }}
            >
              {safeText(row.company)}
            </span>
          </span>
        </label>
      </div>

      <div
        style={{
          display: "grid",
          gap: 7,
          marginTop: 10,
          fontSize: 13,
        }}
      >
        <div>
          <b>Phone:</b>{" "}
          <span style={{ wordBreak: "break-word" }}>
            <WhatsAppPhone value={row.phones} />
          </span>
        </div>

        <div>
          <b>Birthday:</b> {safeText(row.birthday)}
        </div>

        <div>
          <b>Added:</b> {safeText(row.added_at)}
        </div>
        <div><b>Matched date:</b> {(row.matched_dates || [row.search_date]).filter(Boolean).join(", ") || "—"}</div>

        <div>
          <b>Database:</b> {safeText(row.source_db, "table.db")}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 12,
        }}
      >
        <button
          style={{
            ...btn("primary"),
            color: "#111827",
            borderColor: "#cbd5e1",
            flex: "1 1 120px",
          }}
          onClick={onEdit}
        >
          ✏️ Edit
        </button>

        <button
          style={{
            ...btn("danger"),
            color: "#111827",
            borderColor: "#fecaca",
            flex: "1 1 120px",
          }}
          onClick={onDelete}
          disabled={deleting}
        >
          {deleting ? "Deleting…" : "🗑 Delete"}
        </button>
      </div>
    </div>
  );
}

export default function SearchByDate() {
  const { apiFetch, apiJSON, user } = useAuth();
  const { isMobile, isTablet, width } = useViewport();

  const [step, setStep] = useState("branch"); // branch | date | list
  const [workflow, setWorkflow] = useState("followup");
  const [followupBusy, setFollowupBusy] = useState(false);

  const [manualBranch, setManualBranch] = useState(() => {
    try {
      return String(
        localStorage.getItem("manualBranch") || getActiveBranch?.() || ""
      )
        .trim()
        .toLowerCase();
    } catch {
      return "";
    }
  });

  const [date, setDate] = useState(() => todayISO());
  const [dateTo, setDateTo] = useState(() => todayISO());
  const [searchedRange, setSearchedRange] = useState(null);
  const [searchProgress, setSearchProgress] = useState("");
  const [results, setResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [q, setQ] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [sendBusy, setSendBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [jobId, setJobId] = useState("");
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  const [job, setJob] = useState(null);
  const [jobLogs, setJobLogs] = useState([]);
  const pollRef = useRef(null);
  const searchRunRef = useRef(0);
  const campaignLockRef = useRef("");
  const pendingCampaignRef = useRef(null);
  const [connectionNotice, setConnectionNotice] = useState("");

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [sendPassword, setSendPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [newCompany, setNewCompany] = useState("");
  const [companyDate, setCompanyDate] = useState(() => todayISO());
  const [updateBusy, setUpdateBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const userKey = historyUserKey(user);
  const allowedBranches = useMemo(() => {
    const raw = user?.branches || [];
    const claims = (Array.isArray(raw) ? raw : String(raw).replace(/;/g, ",").split(","))
      .map((value) => String(value).trim().toLowerCase()).filter(Boolean);
    const values = claims.length ? claims : [String(user?.city || "").trim().toLowerCase()];
    return BRANCHES.filter((entry) => values.includes("*") || values.includes(entry.value));
  }, [user]);
  const activeBranch = userKey && allowedBranches.some((entry) => entry.value === manualBranch)
    ? String(manualBranch).trim().toLowerCase() : "";
  const currentScope = `${userKey}:${activeBranch}`;
  const scopeRef = useRef(currentScope);
  scopeRef.current = currentScope;
  const jobStorageKey = `wa:last-customer-service-job:v2:${API}:${userKey}:${activeBranch}`;
  const pendingStorageKey = `wa:pending-customer-service:v2:${API}:${userKey}:${activeBranch}`;

  useEffect(() => {
    const scope = currentScope;
    let cancelled = false;
    searchRunRef.current += 1;
    setWorkflow("followup");
    setFollowupBusy(false);
    setResults([]);
    setSearchedRange(null);
    setSelectedIds(new Set());
    setJob(null);
    setJobId("");
    jobIdRef.current = "";
    setJobLogs([]);
    setError("");
    setNotice("");
    setConnectionNotice("");
    setPasswordOpen(false);
    setSendPassword("");
    setSearchBusy(false);
    setSendBusy(false);
    setTestBusy(false);
    setEditOpen(false);
    setEditRow(null);
    setEditBusy(false);
    setUpdateBusy(false);
    setDeletingId(null);
    setNewCompany("");
    setQ("");
    if (!activeBranch) return () => { cancelled = true; };
    apiJSON(`/customer-service/jobs/latest?branch=${encodeURIComponent(activeBranch)}`, {
      method: "GET", headers: { "x-branch": activeBranch },
    }).then((data) => {
      if (cancelled || scopeRef.current !== scope) return;
      if (jobIdRef.current) return;
      if (data?.job) {
        setJob(data.job);
        setJobId(data.job.job_id || "");
        setJobLogs(Array.isArray(data.job.recent_logs) ? data.job.recent_logs : []);
        try { localStorage.setItem(jobStorageKey, data.job.job_id || ""); } catch {}
      }
    }).catch(() => {
      if (cancelled || scopeRef.current !== scope) return;
      try { setJobId(localStorage.getItem(jobStorageKey) || ""); } catch {}
      setConnectionNotice("Saved progress could not be refreshed. Reconnect and use Refresh; no messages are resent automatically.");
    });
    return () => { cancelled = true; };
    // Server history is authoritative; browser storage is only a recovery hint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScope]);

  useEffect(() => () => { scopeRef.current = ""; searchRunRef.current += 1; }, []);

  const styles = useMemo(() => {
    const wrap = {
      minHeight: "100vh",
      color: C.text,
      padding: isMobile ? 10 : 16,
      lineHeight: 1.45,
      background:
        "radial-gradient(circle at top left, rgba(59,130,246,.20), transparent 34%), radial-gradient(circle at top right, rgba(16,185,129,.14), transparent 30%), #07111f",
    };

    const card = {
      background:
        "linear-gradient(180deg, rgba(17,24,39,.96), rgba(15,23,42,.96))",
      border: `1px solid ${C.border}`,
      borderRadius: isMobile ? 14 : 18,
      padding: isMobile ? 11 : 14,
      boxShadow: "0 18px 55px rgba(0,0,0,.26)",
    };

    const input = {
      background: C.card2,
      border: `1px solid ${C.border}`,
      color: C.text,
      padding: "10px 12px",
      borderRadius: 12,
      outline: "none",
      minHeight: 42,
    };

    const controls = {
      display: "flex",
      gap: 8,
      alignItems: "center",
      flexWrap: "wrap",
    };

    const tableContainer = {
      maxHeight: isMobile ? "none" : isTablet ? 620 : 560,
      overflow: "auto",
      borderRadius: 14,
      border: `1px solid ${C.border}`,
      background: "#fff",
    };

    const th = {
      position: "sticky",
      top: 0,
      background: "#e5e7eb",
      borderBottom: "1px solid #cbd5e1",
      textAlign: "left",
      padding: "11px 12px",
      fontSize: 13,
      color: "#111827",
      zIndex: 1,
      whiteSpace: "nowrap",
    };

    const td = {
      borderBottom: "1px solid #e5e7eb",
      padding: "10px 12px",
      fontSize: 14,
      verticalAlign: "middle",
      color: C.tableText,
      background: "#ffffff",
    };

    return {
      wrap,
      card,
      input,
      controls,
      tableContainer,
      th,
      td,
    };
  }, [isMobile, isTablet]);

  const modalBackdrop = useMemo(
    () => ({
      position: "fixed",
      inset: 0,
      zIndex: 9000,
      background: "rgba(2,6,23,.84)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding:
        "max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left))",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
    }),
    []
  );

  const modalCard = (maxWidth = 720, extra = {}) => ({
    ...styles.card,
    width: `min(calc(100vw - 20px), ${maxWidth}px)`,
    maxWidth: "100%",
    maxHeight: "min(92dvh, calc(100vh - 20px))",
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    wordBreak: "break-word",
    borderRadius: isMobile ? 16 : 22,
    padding: isMobile ? 14 : 18,
    margin: "auto",
    ...extra,
  });

  const btn = (variant = "primary", extra = {}) => {
    const base = {
      border: `1px solid ${C.border}`,
      borderRadius: 13,
      padding: "10px 13px",
      minHeight: 42,
      background: C.accentSoft,
      color: C.text,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      userSelect: "none",
      whiteSpace: "nowrap",
      fontWeight: 800,
      maxWidth: "100%",
      ...(isMobile ? { flex: "1 1 100%" } : {}),
      ...extra,
    };

    if (variant === "ghost") {
      base.background = "rgba(255,255,255,.04)";
    }

    if (variant === "danger") {
      base.background = C.dangerSoft;
      base.border = "1px solid rgba(239,68,68,0.35)";
    }

    if (variant === "success") {
      base.background = C.successSoft;
      base.border = "1px solid rgba(16,185,129,0.38)";
    }

    if (variant === "warning") {
      base.background = C.warningSoft;
      base.border = "1px solid rgba(245,158,11,0.35)";
    }

    return base;
  };

  const tag = (background = C.card2) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: `1px solid ${C.border}`,
    background,
    color: C.text,
  });

  function branchHeaders(extra = {}) {
    return {
      ...extra,
      ...(activeBranch ? { "x-branch": activeBranch } : {}),
    };
  }

  async function apiJSONWithBranch(path, options = {}) {
    return apiJSON(path, {
      ...options,
      headers: branchHeaders(options.headers || {}),
    });
  }

  async function apiFetchWithBranch(url, options = {}) {
    return apiFetch(url, {
      ...options,
      headers: branchHeaders(options.headers || {}),
    });
  }

  async function recoverLatestJob() {
    if (!activeBranch || scopeRef.current !== currentScope) return;
    const recoveryScope = currentScope;
    try {
      const data = await apiJSONWithBranch(`/customer-service/jobs/latest?branch=${encodeURIComponent(activeBranch)}`, { method: "GET" });
      if (scopeRef.current !== recoveryScope) return;
      if (!data?.job) {
        setConnectionNotice("No saved campaign was found for this branch.");
        return;
      }
      setJob(data.job);
      setJobId(data.job.job_id || "");
      setJobLogs(Array.isArray(data.job.recent_logs) ? data.job.recent_logs : []);
      setConnectionNotice("");
      setNotice("Saved campaign recovered. Check the last processed contact and delivery history before starting another campaign.");
      try { localStorage.setItem(jobStorageKey, data.job.job_id || ""); } catch {}
    } catch (err) {
      if (scopeRef.current === recoveryScope) setConnectionNotice(`Saved progress is unavailable. No messages were resent. ${err?.message || ""}`);
    }
  }

  async function readErrorMessage(res) {
    const ct = res.headers.get("content-type") || "";

    if (ct.includes("application/json")) {
      const j = await res.json().catch(() => ({}));
      return j?.detail || j?.message || `${res.status} ${res.statusText}`;
    }

    const t = await res.text().catch(() => "");
    return t || `${res.status} ${res.statusText}`;
  }

  function downloadText(content, type, filename) {
    const blob = new Blob([content], { type });
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = objUrl;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();

    window.setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  }

  function selectedRows() {
    return results.filter((row) => selectedIds.has(getRowKey(row)));
  }

  async function handleExportSelectedVCF() {
    if (selectedIds.size === 0) {
      setError("Select at least one row to export VCF.");
      return;
    }

    setError("");
    setNotice("");

    try {
      const br = activeBranch || "branch";

      downloadText(
        selectedRowsVCF(selectedRows()), "text/vcard;charset=utf-8",
        `guests_${br}_${searchedRange?.from || date}_${searchedRange?.to || dateTo}.vcf`
      );

      setNotice(
        `✅ VCF exported for ${selectedIds.size.toLocaleString()} selected contact(s).`
      );
    } catch (e) {
      console.error(e);
      setError(`VCF export failed. ${e?.message ? `(${e.message})` : ""}`);
    }
  }

  async function handleExportSelectedCSV() {
    if (selectedIds.size === 0) {
      setError("Select at least one row to export CSV.");
      return;
    }

    setError("");
    setNotice("");

    try {
      const br = activeBranch || "branch";

      downloadText(
        `\uFEFF${selectedRowsCSV(selectedRows())}`, "text/csv;charset=utf-8",
        `guests_${br}_${searchedRange?.from || date}_${searchedRange?.to || dateTo}.csv`
      );

      setNotice(
        `✅ CSV exported for ${selectedIds.size.toLocaleString()} selected contact(s).`
      );
    } catch (e) {
      console.error(e);
      setError(`CSV export failed. ${e?.message ? `(${e.message})` : ""}`);
    }
  }

  function normalizeRows(arr) {
    return (arr || []).map((r, i) => {
      const lower = Object.fromEntries(
        Object.entries(r || {}).map(([k, v]) => [
          String(k).toLowerCase(),
          v,
        ])
      );

      const _id =
        r.id ??
        lower.id ??
        r._id ??
        lower._id ??
        r.rowid ??
        lower.rowid ??
        i;

      return {
        _id,
        name: r.name ?? lower.name ?? "",
        company: r.company ?? lower.company ?? "",
        phones: r.phones ?? lower.phones ?? r.phone ?? lower.phone ?? "",
        birthday:
          r.birthday ?? lower.birthday ?? r.bday ?? lower.bday ?? "",
        added_at:
          r.added_at ??
          lower.added_at ??
          r.created_at ??
          lower.created_at ??
          "",
        source_db:
          r.source_db ??
          lower.source_db ??
          r.database ??
          lower.database ??
          r.db_name ??
          lower.db_name ??
          "table.db",
        __raw: r,
      };
    });
  }

  function getRowId(r) {
    return r?._id ?? r?.id ?? null;
  }

  function getRowKey(r) {
    return `${r?.source_db || "table.db"}::${getRowId(r)}::${r?.phones || ""}`;
  }

  function selectBranch(branch) {
    const b = String(branch || "").trim().toLowerCase();
    if (!allowedBranches.some((entry) => entry.value === b)) return;

    setManualBranch(b);

    try {
      localStorage.setItem("manualBranch", b);
      localStorage.setItem("app:activeBranch:v1", b);
      localStorage.setItem("BRANCH", b);
    } catch {}

    setDate(todayISO());
    setDateTo(todayISO());
    setResults([]);
    setSelectedIds(new Set());
    setJob(null);
    setJobId("");
    setJobLogs([]);
    setError("");
    setNotice("");
    setStep("date");
  }

  function extractRows(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.contacts)) return payload.contacts;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  }

  function contactDedupKey(row) {
    return contactIdentity(row);
  }

  function mergeUniqueRows(groups) {
    const seen = new Set();
    const merged = [];

    groups.flat().forEach((row) => {
      const key = contactDedupKey(row);

      if (seen.has(key)) return;

      seen.add(key);
      merged.push(row);
    });

    return merged;
  }

  async function loadDateRowsFromAllDatabases(nextDate) {
    const encodedDate = encodeURIComponent(nextDate);

    try {
      const combined = await apiJSONWithBranch(
        `/table-search/by-date?date=${encodedDate}&include_unmerged=1&scan_sibling_tables=1`,
        { method: "GET" }
      );

      const combinedRows = normalizeRows(extractRows(combined));

      if (combinedRows.length || combined?.scan_supported === true) {
        return {
          rows: combinedRows,
          databases: combined?.databases || combined?.sources || [],
          mode: "combined",
        };
      }
    } catch (combinedError) {
      console.warn("Combined database search unavailable:", combinedError);
    }

    const [mainResult, unmergedResult] = await Promise.allSettled([
      apiJSONWithBranch(
        `/table-search/by-date?date=${encodedDate}`,
        { method: "GET" }
      ),
      apiJSONWithBranch(
        `/table-search/by-date-unmerged?date=${encodedDate}`,
        { method: "GET" }
      ),
    ]);

    if (
      mainResult.status === "rejected" &&
      unmergedResult.status === "rejected"
    ) {
      throw (
        mainResult.reason ||
        unmergedResult.reason ||
        new Error("Date search failed.")
      );
    }

    const mainRows =
      mainResult.status === "fulfilled"
        ? normalizeRows(extractRows(mainResult.value)).map((r) => ({
            ...r,
            source_db: r.source_db || "table.db",
          }))
        : [];

    const unmergedRows =
      unmergedResult.status === "fulfilled"
        ? normalizeRows(extractRows(unmergedResult.value)).map((r) => ({
            ...r,
            source_db: r.source_db || "table_number.db",
          }))
        : [];

    return {
      rows: mergeUniqueRows([mainRows, unmergedRows]),
      databases: Array.from(
        new Set(
          [...mainRows, ...unmergedRows]
            .map((r) => r.source_db)
            .filter(Boolean)
        )
      ),
      mode:
        unmergedResult.status === "fulfilled"
          ? "fallback-merged"
          : "main-only",
    };
  }

  async function handleSearch(nextDate = date, nextTo = dateTo) {
    const searchScope = currentScope;
    if (scopeRef.current !== searchScope) return;
    const run = ++searchRunRef.current;
    setError("");
    setNotice("");
    setResults([]);
    setSelectedIds(new Set());
    setSearchedRange(null);

    if (!activeBranch) {
      setError("Please select a branch first.");
      setStep("branch");
      return;
    }

    let days;
    try { days = inclusiveDateRange(nextDate, nextTo); }
    catch (err) {
      setError(err.message);
      setStep("date");
      return;
    }

    setSearchBusy(true);

    try {
      const groups = [];
      for (let index = 0; index < days.length; index += 4) {
        if (scopeRef.current !== searchScope || searchRunRef.current !== run) return;
        const batch = days.slice(index, index + 4);
        setSearchProgress(`Loading days ${index + 1}–${index + batch.length} of ${days.length}…`);
        const loadedBatch = await Promise.allSettled(batch.map((day) => loadDateRowsFromAllDatabases(day)));
        if (scopeRef.current !== searchScope || searchRunRef.current !== run) return;
        for (let offset = 0; offset < loadedBatch.length; offset += 1) {
          const loaded = loadedBatch[offset];
          if (loaded.status !== "fulfilled") throw new Error(`Could not finish ${batch[offset]}. ${loaded.reason?.message || "Try again."}`);
          groups.push((loaded.value.rows || []).map((row) => ({ ...row, search_date: batch[offset], matched_dates: [batch[offset]] })));
        }
      }
      const rows = mergeDateRangeRows(groups);

      setResults(rows);
      setSearchedRange({ from: nextDate, to: nextTo });
      setStep("list");

      const sources = Array.from(
        new Set(
          rows
            .map((r) => String(r.source_db || "table.db"))
            .filter(Boolean)
        )
      );

      if (!rows.length) {
        setError(
          "No contacts found in this date range. Try different dates or another branch."
        );
      } else {
        const sourceText = sources.length
          ? sources.join(", ")
          : "table.db";

        setNotice(
          `Loaded ${rows.length.toLocaleString()} guests from ${nextDate} to ${nextTo}. Each guest appears once; the earliest matching date is saved for sending. Sources: ${sourceText}.`
        );
      }
    } catch (err) {
      if (scopeRef.current !== searchScope || searchRunRef.current !== run) return;
      console.error(err);
      setStep("date");

      setError(
        `Failed to load contacts from table.db and table_number.db. ${
          err?.message ? `(${err.message})` : ""
        }`
      );
    } finally {
      if (scopeRef.current === searchScope && searchRunRef.current === run) {
        setSearchBusy(false);
        setSearchProgress("");
      }
    }
  }

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();

    if (!qq) return results;

    return results.filter((r) =>
      [
        r.name,
        r.company,
        r.phones,
        r.birthday,
        r.added_at,
        r.source_db,
      ].some((x) => String(x || "").toLowerCase().includes(qq))
    );
  }, [results, q]);

  const selectedCount = selectedIds.size;

  const selectedInViewCount = filtered.filter((r) =>
    selectedIds.has(getRowKey(r))
  ).length;

  const allShownSelected =
    filtered.length > 0 && selectedInViewCount === filtered.length;

  const intendedSendCount = selectedCount || filtered.length;

  function toggleOne(row) {
    const id = getRowKey(row);

    if (id === null || id === undefined) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  }

  function toggleSelectAllShown() {
    if (!filtered.length) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allShownSelected) {
        filtered.forEach((r) => next.delete(getRowKey(r)));
      } else {
        filtered.forEach((r) => {
          const id = getRowKey(r);
          if (id !== null && id !== undefined) next.add(id);
        });
      }

      return next;
    });
  }

  function openSendPassword() {
    setError("");
    setNotice("");

    if (!activeBranch) {
      return setError("Please select a branch first.");
    }

    if (!date) {
      return setError("Please choose a date first.");
    }

    if (!filtered.length) {
      return setError("No contacts available. Search first.");
    }

    setSendPassword("");
    setPasswordError("");
    setPasswordOpen(true);
  }

  async function startCustomerServiceSending() {
    const sendScope = currentScope;
    if (scopeRef.current !== sendScope) return;
    if (campaignLockRef.current === sendScope) return;
    setError("");
    setNotice("");
    setPasswordError("");

    if (sendPassword !== SEND_PASSWORD) {
      setPasswordError("Wrong password. 360 sending is locked.");
      return;
    }

    const chosenRows = selectedCount
      ? results.filter((row) => selectedIds.has(getRowKey(row)))
      : filtered;
    const ids = chosenRows.map(getRowId).filter((x) => x !== null && x !== undefined);

    if (!ids.length) {
      setPasswordError("No contacts selected or visible.");
      return;
    }

    if (!activeBranch || !searchedRange) {
      setPasswordError("Choose your branch and search a date range first.");
      return;
    }
    const contacts = chosenRows.map((row) => ({
      id: getRowId(row), name: row.name || "",
      phone: dateSearchRecipientPhone(row.phone || row.phones || ""), source_db: row.source_db || "table.db",
      search_date: row.search_date || searchedRange.from,
    }));
    const fingerprint = JSON.stringify({ from: searchedRange.from, to: searchedRange.to, contacts, dryRun });
    let requestId = pendingCampaignRef.current?.scope === sendScope && pendingCampaignRef.current?.fingerprint === fingerprint
      ? pendingCampaignRef.current.requestId : newRetryKey();
    try {
      const saved = JSON.parse(localStorage.getItem(pendingStorageKey) || "null");
      if (saved?.fingerprint === fingerprint && saved?.requestId) requestId = saved.requestId;
      localStorage.setItem(pendingStorageKey, JSON.stringify({ fingerprint, requestId }));
    } catch {}
    pendingCampaignRef.current = { scope: sendScope, fingerprint, requestId };
    campaignLockRef.current = sendScope;
    setSendBusy(true);

    try {
      const data = await apiJSONWithBranch(
        `/customer-service/send-by-date`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: searchedRange.from,
            date_from: searchedRange.from,
            date_to: searchedRange.to,
            source: "customer_service",
            search_date: searchedRange.from,
            request_id: requestId,
            password: sendPassword,
            ids,
            contacts,
            dry_run: dryRun,
            valid_only: true,
            exclude_bad_names: true,
            remove_duplicates: true,
          }),
        }
      );
      if (scopeRef.current !== sendScope) return;
      setPasswordOpen(false);
      pendingCampaignRef.current = null;
      setJobId(data.job_id);
      try {
        localStorage.setItem(jobStorageKey, String(data.job_id || ""));
        localStorage.removeItem(pendingStorageKey);
      } catch {}
      setJob(data);
      setJobLogs([]);

      setNotice(
        `✅ Campaign started. Job ${data.job_id}. Total valid customers: ${num(data.total)}.`
      );
    } catch (err) {
      if (scopeRef.current !== sendScope) return;
      console.error(err);
      setPasswordError(
        `The start response was not confirmed. Recover saved progress before sending again. Repeating this unchanged request on this page uses the same request ID. ${err?.message || ""}`
      );
    } finally {
      if (campaignLockRef.current === sendScope) campaignLockRef.current = "";
      if (scopeRef.current === sendScope) setSendBusy(false);
    }
  }

  async function sendTestMessage() {
    const sendScope = currentScope;
    if (scopeRef.current !== sendScope) return;
    if (campaignLockRef.current === sendScope) return;
    setError("");
    setNotice("");
    setPasswordError("");

    if (sendPassword !== SEND_PASSWORD) {
      setPasswordError("Wrong password. Test sending is locked.");
      return;
    }

    if (!activeBranch) {
      setPasswordError("Please select a branch first.");
      return;
    }

    campaignLockRef.current = sendScope;
    setTestBusy(true);

    try {
      const data = await apiJSONWithBranch(
        `/customer-service/test-send`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            password: sendPassword,
            phone: TEST_SEND_PHONE,
            name: "Test",
          }),
        }
      );
      if (scopeRef.current !== sendScope) return;
      setNotice(
        data?.message || `✅ Test message sent to ${TEST_SEND_PHONE}.`
      );
    } catch (err) {
      if (scopeRef.current !== sendScope) return;
      console.error(err);
      setPasswordError(`Test send failed. ${err?.message || ""}`);
    } finally {
      if (campaignLockRef.current === sendScope) campaignLockRef.current = "";
      if (scopeRef.current === sendScope) setTestBusy(false);
    }
  }

  async function stopCampaign() {
    const stopScope = currentScope;
    if (!jobId || scopeRef.current !== stopScope) return;

    try {
      const data = await apiJSONWithBranch(
        `/customer-service/stop/${encodeURIComponent(jobId)}`,
        { method: "POST" }
      );
      if (scopeRef.current !== stopScope) return;
      setNotice(data?.message || "Stop requested.");
      await refreshJobStatus(jobId);
    } catch (err) {
      if (scopeRef.current !== stopScope) return;
      setError(`Failed to stop campaign. ${err?.message || ""}`);
    }
  }

  async function refreshJobStatus(id = jobId) {
    const statusScope = currentScope;
    if (!id || scopeRef.current !== statusScope) return;

    try {
      const data = await apiJSONWithBranch(
        `/customer-service/status/${encodeURIComponent(id)}`,
        { method: "GET" }
      );
      if (scopeRef.current !== statusScope || jobIdRef.current !== id) return;
      setConnectionNotice("");
      setJob(data);
      setJobLogs(
        Array.isArray(data?.recent_logs) ? data.recent_logs : []
      );

      if (["completed", "stopped", "failed"].includes(data?.status)) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      if (scopeRef.current === statusScope && jobIdRef.current === id) setConnectionNotice("Connection lost. The server may still be sending. Reconnect and refresh saved progress; do not start another campaign to recover.");
    }
  }

  useEffect(() => {
    if (!jobId) return;

    refreshJobStatus(jobId);

    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(
      () => refreshJobStatus(jobId),
      1500
    );

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, currentScope]);

  async function handleUpdateCompany() {
    const editScope = currentScope;
    if (scopeRef.current !== editScope) return;
    setNotice("");
    setError("");

    if (!newCompany || !companyDate) {
      setNotice("Please enter a company and select a date.");
      return;
    }

    try {
      if (!activeBranch) {
        return setNotice("❌ No active branch found.");
      }

      setUpdateBusy(true);

      const form = new FormData();
      form.append("date", companyDate);
      form.append("company", newCompany);

      const res = await apiFetchWithBranch(
        `${API}/update-company-by-date`,
        {
          method: "POST",
          body: form,
        }
      );

      if (!res.ok) throw new Error(await readErrorMessage(res));

      const data = await res.json().catch(() => ({}));
      if (scopeRef.current !== editScope) return;
      setNotice(data?.message || "✅ Updated successfully.");
      await handleSearch();
    } catch (err) {
      if (scopeRef.current !== editScope) return;
      setNotice(`❌ ${err?.message || "Failed to update."}`);
    } finally {
      if (scopeRef.current === editScope) setUpdateBusy(false);
    }
  }

  function openEdit(row) {
    setEditRow({ ...row });
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    setEditRow(null);
  }

  async function saveEdit() {
    if (!editRow) return;
    const editScope = currentScope;
    if (scopeRef.current !== editScope) return;

    try {
      if (!activeBranch) {
        return setNotice("❌ No active branch found.");
      }

      setEditBusy(true);

      const id = editRow._id ?? editRow.id;

      if (!id && id !== 0) {
        throw new Error("Missing id on row.");
      }

      const data = await apiJSONWithBranch(`/update-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id,
          name: editRow.name || "",
          company: editRow.company || "",
          phones: editRow.phones || "",
          birthday: editRow.birthday || "",
        }),
      });
      if (scopeRef.current !== editScope) return;
      setNotice(data?.message || "✅ Updated in table.db");
      await handleSearch();
      closeEdit();
    } catch (e) {
      if (scopeRef.current !== editScope) return;
      setNotice(`❌ ${e?.message || "Failed to update."}`);
    } finally {
      if (scopeRef.current === editScope) setEditBusy(false);
    }
  }

  async function handleDelete(row) {
    const editScope = currentScope;
    if (scopeRef.current !== editScope) return;
    const id = row?._id ?? row?.id;

    if (!id && id !== 0) {
      return setNotice("❌ Missing id.");
    }

    if (!window.confirm(`Delete contact id ${id} from table.db?`)) {
      return;
    }

    try {
      setDeletingId(id);

      const res = await apiFetchWithBranch(
        `${API}/delete-contact?id=${encodeURIComponent(id)}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error(await readErrorMessage(res));
      if (scopeRef.current !== editScope) return;
      setNotice("🗑 Deleted from table.db");
      await handleSearch();
    } catch (e) {
      if (scopeRef.current !== editScope) return;
      setNotice(`❌ ${e?.message || "Failed to delete."}`);
    } finally {
      if (scopeRef.current === editScope) setDeletingId(null);
    }
  }

  const progress = pct(job);
  const completed = getCompleted(job);
  const isRunning = job && ["queued", "running"].includes(job.status);

  return (
    <div style={styles.wrap}>
      <div
        style={{
          maxWidth: 1420,
          margin: "0 auto",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 14,
            marginBottom: 14,
            flexWrap: "wrap",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 15,
                display: "grid",
                placeItems: "center",
                background: C.accentSoft,
                border: `1px solid ${C.borderStrong}`,
              }}
            >
              📨
            </div>

            <div>
              <h2
                style={{
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 900,
                  margin: 0,
                }}
              >
                Guest follow-ups
              </h2>

              <div style={{ fontSize: 13, color: C.textDim }}>First bv_1 → no reply → Happy birthday 🎂</div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {activeBranch && (
              <span style={tag(C.accentSoft)}>
                Branch: {activeBranch}
              </span>
            )}

            {workflow === "other" && <>
            {date && <span style={tag()}>From {searchedRange?.from || date} to {searchedRange?.to || dateTo}</span>}

            <button
              style={btn("ghost")}
              onClick={() => setStep("branch")}
            >
              Change branch
            </button>

            <button
              style={btn("ghost")}
              onClick={() => setStep("date")}
              disabled={!activeBranch}
            >
              Choose dates
            </button>
            <button style={btn("ghost")} onClick={recoverLatestJob} disabled={!activeBranch}>Recover saved campaign</button>
            </>}
          </div>
        </div>

        <div style={{ ...styles.card, marginBottom: 14, ...styles.controls }}>
          <label style={{ display: "grid", gap: 5 }}>Branch<select aria-label="Follow-up branch" value={activeBranch} disabled={followupBusy || sendBusy || testBusy || searchBusy} onChange={(event) => selectBranch(event.target.value)} style={styles.input}>
            <option value="">Choose branch</option>{allowedBranches.map(entry => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
          </select></label>
          <button type="button" aria-pressed={workflow === "followup"} style={btn(workflow === "followup" ? "success" : "ghost")} disabled={followupBusy || sendBusy || testBusy} onClick={() => { setWorkflow("followup"); setPasswordOpen(false); setEditOpen(false); }}>Follow up non-repliers</button>
          <button type="button" aria-pressed={workflow === "other"} style={btn("ghost")} disabled={followupBusy || sendBusy || testBusy} onClick={() => setWorkflow("other")}>Other date tools · all guests</button>
          {!allowedBranches.length && <span role="status">Your account has no assigned branch. Ask your administrator to update your access.</span>}
        </div>

        {workflow === "followup" && <>
          <Bv1FollowUp branch={activeBranch} userKey={userKey} apiJSON={apiJSON} defaultFrom={date} defaultTo={dateTo} onBusyChange={setFollowupBusy} />
          <details style={styles.card}><summary style={{ cursor: "pointer", fontWeight: 700 }}>Saved sending history &amp; error details</summary><MessageHistory branch={activeBranch} source="birthday" userKey={userKey} apiJSON={apiJSON} refreshToken={followupBusy} /></details>
        </>}

        {workflow === "other" && <>
        <p style={{ color: C.warning }}>These original tools can send to all selected guests. Use “Follow up non-repliers” for the first bv_1 reply check.</p>

        {(notice || error) && (
          <div
            style={{
              ...styles.card,
              marginBottom: 12,
              borderColor: error
                ? "rgba(239,68,68,.45)"
                : "rgba(16,185,129,.35)",
            }}
          >
            <b>{error ? "❗ Error" : "✅ Status"}:</b>{" "}
            {error || notice}
          </div>
        )}

        <ReplyTracker
          branch={activeBranch}
          source="customer_service"
          userKey={userKey}
          apiJSON={apiJSON}
          defaultFrom={searchedRange?.from || date}
          defaultTo={searchedRange?.to || dateTo}
          refreshToken={`${jobId}-${job?.status || ""}-${job?.sent || 0}-${job?.failed || 0}`}
        />

        {connectionNotice && <div role="status" style={{ ...styles.card, marginBottom: 12, color: C.warning }}>{connectionNotice}</div>}

        <MessageHistory
          branch={activeBranch}
          source="customer_service"
          userKey={historyUserKey(user)}
          apiJSON={apiJSON}
          refreshToken={`${jobId}-${job?.status || ""}-${job?.sent || 0}-${job?.failed || 0}`}
        />

        {step === "branch" && (
          <div style={{ ...styles.card, marginBottom: 12 }}>
            <div
              style={{ maxWidth: 820 }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "clamp(20px, 4vw, 28px)",
                }}
              >
                Select Branch
              </h2>

              <p style={{ color: C.textDim, marginTop: 6 }}>
                Choose the branch whose guests you want to contact.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {allowedBranches.map((b) => (
                  <button
                    key={b.value}
                    style={btn("primary", {
                      minHeight: 66,
                      fontSize: "clamp(15px, 2.5vw, 18px)",
                      width: "100%",
                      flex: "1 1 auto",
                    })}
                    onClick={() => selectBranch(b.value)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              {!allowedBranches.length && <p role="status">Your account has no assigned branch. Ask your administrator to update your access.</p>}
            </div>
          </div>
        )}

        {step === "date" && (
          <div style={{ ...styles.card, marginBottom: 12 }}>
            <div
              style={{ maxWidth: 720 }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "clamp(20px, 4vw, 28px)",
                }}
              >
                Choose a date range
              </h2>

              <p style={{ color: C.textDim, marginTop: 6 }}>
                Branch: <b>{activeBranch}</b>. Both dates are included. Use the same date in both fields for one day.
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                <label style={{ flex: "1 1 220px", display: "grid", gap: 5 }}>
                  From
                  <input type="date" value={date} onChange={(e) => {
                    setDate(e.target.value);
                    if (e.target.value > dateTo) setDateTo(e.target.value);
                  }} style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} />
                </label>
                <label style={{ flex: "1 1 220px", display: "grid", gap: 5 }}>
                  To
                  <input type="date" value={dateTo} min={date || undefined} onChange={(e) => setDateTo(e.target.value)} style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} />
                </label>
              </div>
              <p style={{ fontSize: 12, color: C.muted }}>Search up to 366 days. A guest found on several dates appears once.</p>

              <div
                style={{
                  ...styles.controls,
                  justifyContent: "space-between",
                  marginTop: 14,
                }}
              >
                <button
                  style={btn("ghost")}
                  onClick={() => setStep("branch")}
                >
                  ← Back to branch
                </button>

                <button
                  style={btn("success")}
                  disabled={!activeBranch || !date || !dateTo || searchBusy}
                  onClick={() => handleSearch(date, dateTo)}
                >
                  {searchBusy
                    ? searchProgress || "Loading…"
                    : "Find guests"}
                </button>
                <button style={btn("ghost")} onClick={() => setStep("list")}>View saved progress</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...styles.card, marginBottom: 12 }}>
          <div style={styles.controls}>
            <input
              aria-label="Filter guests"
              placeholder="Filter name, company, phone, birthday, database…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                ...styles.input,
                minWidth: isMobile ? "100%" : 260,
                flex: "1 1 260px",
                width: isMobile ? "100%" : undefined,
              }}
            />

            <button
              style={btn("primary")}
              onClick={toggleSelectAllShown}
              disabled={!filtered.length}
            >
              {allShownSelected
                ? "⛔ Unselect shown"
                : "☑️ Select shown"}
            </button>

            <button
              style={btn("ghost")}
              onClick={() => setSelectedIds(new Set())}
              disabled={!selectedCount}
            >
              Clear selection
            </button>

            <button
              style={btn("success")}
              onClick={openSendPassword}
              disabled={!filtered.length || sendBusy}
            >
              Send to {selectedCount ? "selected" : "shown"} guests ({num(intendedSendCount)})
            </button>

            <button
              style={btn("warning")}
              onClick={openSendPassword}
              disabled={!activeBranch || sendBusy || testBusy}
            >
              🧪 Test 360 to {TEST_SEND_PHONE}
            </button>

            <button
              style={btn("success")}
              onClick={handleExportSelectedVCF}
              disabled={!selectedCount}
            >
              ✅📤 Export Selected VCF ({num(selectedCount)})
            </button>

            <button
              style={btn("success")}
              onClick={handleExportSelectedCSV}
              disabled={!selectedCount}
            >
              ✅🧾 Export Selected CSV ({num(selectedCount)})
            </button>
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={tag()}>
              Fetched: {num(results.length)}
            </span>

            <span style={tag(C.accentSoft)}>
              Shown: {num(filtered.length)}
            </span>

            <span style={tag(C.successSoft)}>
              Selected: {num(selectedCount)}
            </span>

            <label
              style={{
                ...tag(C.warningSoft),
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry run
            </label>
          </div>
        </div>

        {job && (
          <div
            style={{
              ...styles.card,
              marginBottom: 12,
              borderColor: isRunning
                ? "rgba(245,158,11,.42)"
                : "rgba(16,185,129,.42)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  Saved sending progress
                </h3>

                <div style={{ color: C.textDim, fontSize: 13 }}>
                  Job <b>{job.job_id || jobId}</b> · Status{" "}
                  <b>{job.status}</b> · Completed {num(completed)} /{" "}
                  {num(job.total)}
                </div>
              </div>

              <div style={styles.controls}>
                <button
                  style={btn("danger")}
                  onClick={stopCampaign}
                  disabled={!isRunning}
                >
                  🛑 Stop
                </button>

                <button
                  style={btn("primary")}
                  onClick={() => refreshJobStatus()}
                  disabled={!jobId}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                height: 15,
                background: C.card2,
                borderRadius: 999,
                overflow: "hidden",
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  background:
                    "linear-gradient(90deg, rgba(16,185,129,.8), rgba(59,130,246,.85))",
                  transition: "width .25s ease",
                }}
              />
            </div>

            <div
              style={{
                marginTop: 10,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(135px, 1fr))",
                gap: 8,
              }}
            >
              {[
                ["Progress", `${progress}%`],
                ["Total", num(job.total)],
                ["Provider accepted", num(job.sent)],
                ["Failed", num(job.failed)],
                ["Skipped", num(job.skipped)],
                [
                  "Current",
                  `${num(job.current_index)} / ${num(job.total)}`,
                ],
              ].map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    background: C.card2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                    padding: 10,
                  }}
                >
                  <div style={{ color: C.muted, fontSize: 12 }}>
                    {k}
                  </div>

                  <div style={{ fontSize: 20, fontWeight: 900 }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>

            <p style={{ color: C.muted, fontSize: 12 }}>Accepted means the provider received the request. Delivery and replies are confirmed separately by webhook.</p>

            {job.last_processed && <div style={{ marginTop: 10, color: C.textDim }}>
              Last processed: <b>{job.last_processed.name || "Guest"}</b> · <WhatsAppPhone value={job.last_processed.phone} color={C.success} /> · {job.last_processed.status} · {job.last_processed.search_date || ""}
            </div>}
            {job.last_accepted && <div style={{ marginTop: 8, color: C.textDim }}>
              Last provider accepted: <b>{job.last_accepted.name || "Guest"}</b> · <WhatsAppPhone value={job.last_accepted.phone} color={C.success} />
            </div>}

            {isRunning && job.current_name && (
              <div style={{ marginTop: 10, color: C.textDim }}>
                Now sending: <b>{job.current_name}</b>{" "}
                {job.current_phone ? (
                  <>
                    (
                    <WhatsAppPhone
                      value={job.current_phone}
                      color={C.success}
                    />
                    )
                  </>
                ) : (
                  ""
                )}
              </div>
            )}

            {!!jobLogs.length && (
              <div style={{ marginTop: 12 }}>
                <b>Recent activity</b>

                <div
                  style={{
                    marginTop: 8,
                    display: "grid",
                    gap: 6,
                    maxHeight: 210,
                    overflow: "auto",
                  }}
                >
                  {jobLogs
                    .slice()
                    .reverse()
                    .map((l, i) => (
                      <div
                        key={i}
                        style={{
                          background: C.card2,
                          border: `1px solid ${C.border}`,
                          borderRadius: 12,
                          padding: "8px 10px",
                          fontSize: 13,
                          color: C.textDim,
                        }}
                      >
                        <b
                          style={{
                            color:
                              ["Sent", "Accepted", "ACCEPTED", "SENT", "DELIVERED", "READ"].includes(l.status)
                                ? C.success
                                : l.status === "Failed"
                                ? C.danger
                                : C.warning,
                          }}
                        >
                          {["Sent", "Accepted", "ACCEPTED"].includes(l.status) ? "Provider accepted" : l.status}
                        </b>
                        {" · "}
                        {l.name || "Guest"} ·{" "}
                        <WhatsAppPhone
                          value={l.normalized_phone || l.phone}
                          color={C.success}
                          fallback=""
                        />{" "}
                        {l.http_code
                          ? `· HTTP ${l.http_code}`
                          : ""}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={styles.card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <b>Customer List</b>

            <span style={{ fontSize: 12, color: C.textDim }}>
              Search includes table.db and nearby unmerged
              table_number.db files. If no rows are selected, 360 Send
              uses all shown rows.
            </span>
          </div>

          {!isMobile ? (
            <div style={styles.tableContainer}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  minWidth: 860,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 72 }}>
                      Select
                    </th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Company</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>Birthday</th>
                    <th style={styles.th}>Added At</th>
                    <th style={styles.th}>Matched date</th>
                    <th style={styles.th}>Database</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((c, i) => {
                    const rid = getRowId(c);

                    const checked =
                      rid !== null && rid !== undefined
                        ? selectedIds.has(getRowKey(c))
                        : false;

                    return (
                      <tr key={getRowKey(c)}>
                        <td style={styles.td}>
                          <input
                            type="checkbox"
                            aria-label={`Select ${safeText(c.name, "guest")}`}
                            checked={checked}
                            onChange={() => toggleOne(c)}
                            style={{
                              transform: "scale(1.15)",
                              cursor: "pointer",
                            }}
                          />
                        </td>

                        <td style={styles.td}>
                          <b>{safeText(c.name)}</b>
                        </td>

                        <td style={styles.td}>
                          {safeText(c.company)}
                        </td>

                        <td style={styles.td}>
                          <WhatsAppPhone value={c.phones} />
                        </td>

                        <td style={styles.td}>
                          {safeText(c.birthday)}
                        </td>

                        <td style={styles.td}>
                          {safeText(c.added_at)}
                        </td>
                        <td style={styles.td}>{(c.matched_dates || [c.search_date]).filter(Boolean).join(", ") || "—"}</td>

                        <td style={styles.td}>
                          {safeText(c.source_db, "table.db")}
                        </td>

                        <td
                          style={{
                            ...styles.td,
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            style={{
                              ...btn("primary"),
                              color: "#111827",
                              borderColor: "#cbd5e1",
                            }}
                            onClick={() => openEdit(c)}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            style={{
                              ...btn("danger"),
                              color: "#111827",
                              borderColor: "#fecaca",
                            }}
                            onClick={() => handleDelete(c)}
                            disabled={deletingId === rid}
                          >
                            {deletingId === rid
                              ? "Deleting…"
                              : "🗑 Delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {filtered.map((c, i) => {
                const rid = getRowId(c);

                const checked =
                  rid !== null && rid !== undefined
                    ? selectedIds.has(getRowKey(c))
                    : false;

                return (
                  <CustomerMobileCard
                    key={getRowKey(c)}
                    row={c}
                    checked={checked}
                    onToggle={() => toggleOne(c)}
                    onEdit={() => openEdit(c)}
                    onDelete={() => handleDelete(c)}
                    deleting={deletingId === rid}
                    btn={btn}
                    C={C}
                  />
                );
              })}
            </div>
          )}

          {!filtered.length && (
            <p style={{ color: C.textDim, marginTop: 10 }}>
              No contacts shown. Select branch/date or adjust the
              filter.
            </p>
          )}
        </div>

        <div style={{ ...styles.card, marginTop: 12 }}>
          <h4 style={{ margin: 0 }}>
            Update company for one date
          </h4>

          <div style={{ ...styles.controls, marginTop: 8 }}>
            <label style={{ display: "grid", gap: 4 }}>Update date
              <input type="date" value={companyDate} onChange={(e) => setCompanyDate(e.target.value)} style={styles.input} />
            </label>
            <input
              type="text"
              placeholder="Enter company name"
              value={newCompany}
              onChange={(e) => setNewCompany(e.target.value)}
              style={{ ...styles.input, minWidth: 240 }}
            />

            <button
              style={btn("success")}
              onClick={handleUpdateCompany}
              disabled={updateBusy || !companyDate || !newCompany}
            >
              {updateBusy ? "Saving…" : `Apply to ${companyDate || "chosen date"}`}
            </button>
          </div>
        </div>
        </>}
      </div>

      {workflow === "other" && passwordOpen && (
        <div style={{ ...modalBackdrop, zIndex: 9999 }}>
          <div
            style={modalCard(650, {
              borderColor: "rgba(16,185,129,.45)",
            })}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "clamp(20px, 4vw, 28px)",
              }}
            >
              🔐 Confirm 360 Sending
            </h2>

            <p style={{ color: C.textDim }}>
              This action is password locked. You can either send one
              test message to <b>{TEST_SEND_PHONE}</b> or start the
              full campaign for <b>{num(intendedSendCount)}</b>{" "}
              customer(s), branch <b>{activeBranch}</b>, from{" "}
              <b>{searchedRange?.from || date}</b> to <b>{searchedRange?.to || dateTo}</b>.
            </p>

            <input
              type="password"
              autoFocus
              placeholder="Enter password"
              value={sendPassword}
              onChange={(e) => setSendPassword(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && startCustomerServiceSending()
              }
              style={{
                ...styles.input,
                width: "100%",
                fontSize: "clamp(16px, 3vw, 18px)",
                boxSizing: "border-box",
              }}
            />

            {passwordError && (
              <div
                style={{
                  marginTop: 10,
                  color: C.danger,
                  fontWeight: 800,
                }}
              >
                {passwordError}
              </div>
            )}

            <div
              style={{
                ...styles.controls,
                justifyContent: "space-between",
                marginTop: 14,
              }}
            >
              <button
                style={btn("ghost")}
                disabled={sendBusy || testBusy}
                onClick={() => setPasswordOpen(false)}
              >
                Cancel
              </button>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <button
                  style={btn("warning")}
                  disabled={testBusy || sendBusy || !sendPassword}
                  onClick={sendTestMessage}
                >
                  {testBusy
                    ? "Sending test…"
                    : `🧪 Send Test to ${TEST_SEND_PHONE}`}
                </button>

                <button
                  style={btn("success")}
                  disabled={sendBusy || testBusy || !sendPassword}
                  onClick={startCustomerServiceSending}
                >
                  {sendBusy
                    ? "Starting…"
                    : dryRun
                    ? "Start Dry Run"
                    : "Unlock & Start Sending"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {workflow === "other" && editOpen && (
        <div style={{ ...modalBackdrop, zIndex: 9998 }}>
          <div style={modalCard(650)}>
            <h3 style={{ marginTop: 0 }}>Adjust Contact</h3>

            <div style={{ display: "grid", gap: 10 }}>
              {[
                ["Name", "name"],
                ["Phone(s)", "phones"],
                ["Birthday", "birthday"],
                ["Company", "company"],
              ].map(([label, key]) => (
                <label
                  key={key}
                  style={{ display: "grid", gap: 6 }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: C.textDim,
                    }}
                  >
                    {label}
                  </span>

                  <input
                    style={styles.input}
                    value={editRow?.[key] || ""}
                    onChange={(e) =>
                      setEditRow((r) => ({
                        ...r,
                        [key]: e.target.value,
                      }))
                    }
                  />
                </label>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginTop: 14,
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 12, color: C.textDim }}>
                Row id:{" "}
                <b>
                  {editRow?._id ?? editRow?.id ?? "(missing)"}
                </b>
              </div>

              <div style={styles.controls}>
                <button
                  style={btn("ghost")}
                  onClick={closeEdit}
                  disabled={editBusy}
                >
                  Cancel
                </button>

                <button
                  style={btn("success")}
                  onClick={saveEdit}
                  disabled={editBusy}
                >
                  {editBusy ? "Saving…" : "💾 Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
